// ─────────────────────────────────────────────────────────────────────────────
// Remplace tes deux composants SearchableMultiSelect et MultiSelect
// par ces versions propres basées sur Popover + Command (shadcn/ui).
//
// ✅ Positionnement correct (Radix gère le placement automatiquement)
// ✅ Pas de débordement de page
// ✅ Recherche intégrée via CommandInput
// ✅ Même API qu'avant : label, options, selectedIds, onToggle, placeholder
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Check, ChevronsUpDown, XCircle } from "lucide-react";

import { cn } from "../../lib/utils";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";

// ─── Composant partagé (avec ou sans barre de recherche) ─────────────────────

function MultiSelectBase({ label, options, selectedIds, onToggle, placeholder, searchable = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedItems = options.filter((opt) => selectedIds.includes(opt.id));

  // If there are a lot of options, only render a slice when the user hasn't typed a query.
  const TRUNCATE_LIMIT = 150;

  const normalizedQuery = (query || "").trim().toLowerCase();

  const filteredOptions = normalizedQuery
    ? options.filter((opt) => opt.name.toLowerCase().includes(normalizedQuery))
    : options;

  // When searching, also limit the number of rendered results to avoid massive renders
  const SEARCH_LIMIT = TRUNCATE_LIMIT;
  const displayedOptions = normalizedQuery ? filteredOptions.slice(0, SEARCH_LIMIT) : options.slice(0, TRUNCATE_LIMIT);

  return (
    <div className="space-y-2">
      {label && <Label className="text-foreground">{label}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            title={placeholder}
            className={cn(
              "w-full justify-between h-auto min-h-10 font-normal",
              "border-border/50 bg-card/90 backdrop-blur-sm hover:bg-accent/50"
            )}
          >
            {selectedItems.length === 0 ? (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1 py-0.5 min-w-0">
                {selectedItems.map((item) => (
                  <Badge
                    key={item.id}
                    variant="secondary"
                    className="inline-flex items-center gap-1 text-xs max-w-full truncate hover:bg-destructive/50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggle(item.id);
                    }}
                  >
                    {item.name}
                  </Badge>
                ))}
              </div>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
          style={{ maxHeight: "min(320px, var(--radix-popover-available-height, 320px))" }}
        >
          <Command>
            {searchable && (
              <CommandInput
                placeholder="Rechercher..."
                className="h-9"
                onValueChange={(value) => setQuery(value)}
              />
            )}
            <CommandList>
              <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
              <CommandGroup>
                {displayedOptions.map((option) => {
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <CommandItem
                      key={option.id}
                      value={option.name}
                      onSelect={() => onToggle(option.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          isSelected ? "opacity-100 text-primary" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{option.name}</span>
                    </CommandItem>
                  );
                })}

                {/* Inform the user that the list was truncated when no query is present */}
                {!normalizedQuery && options.length > TRUNCATE_LIMIT && (
                  <CommandItem
                    key="_truncated_info"
                    value=""
                    onSelect={() => { /* noop */ }}
                    className="cursor-default pointer-events-none text-sm text-muted-foreground"
                  >
                    Affichage limité à {TRUNCATE_LIMIT} résultats. Tapez pour affiner votre recherche.
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Exports ─────────────────────────────────────────────────────────────────

/**
 * Avec barre de recherche intégrée — idéal pour les longues listes (localités…)
 * Même API qu'avant : label, options, selectedIds, onToggle, placeholder
 */
export function SearchableMultiSelect(props) {
  return <MultiSelectBase {...props} searchable />;
}

/**
 * Sans barre de recherche — pour les listes courtes (types d'appels, organismes…)
 * Même API qu'avant : label, options, selectedIds, onToggle, placeholder
 */
export function MultiSelect(props) {
  return <MultiSelectBase {...props} searchable={false} />;
}